import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";

const VANTAGGI = [
  { titolo: "Competenze sempre al passo", desc: "Il personale è costantemente aggiornato su nuove tendenze, tecnologie e metodologie, migliorando l'efficacia dell'azienda." },
  { titolo: "Maggiore competitività", desc: "Le aziende che investono in formazione specializzata riescono a migliorare la qualità del servizio offerto e rispondere rapidamente alle richieste del mercato." },
  { titolo: "Elevata qualificazione professionale", desc: "I corsi permettono di acquisire competenze avanzate, incrementando la professionalità e permettendo di assumere ruoli di maggiore responsabilità." },
  { titolo: "Supporto a specifiche esigenze", desc: "Promo.Ter e Confcommercio Roma propongono corsi mirati alle necessità di ogni singola impresa, ottimizzando il rendimento del team." },
];

const STEPS = [
  { n: "1", titolo: "Consulenza Personalizzata", desc: "Consulenze specifiche per identificare le competenze da sviluppare o aggiornare, suggerendo corsi in linea con le esigenze aziendali." },
  { n: "2", titolo: "Selezione dei Corsi", desc: "Collaboriamo con enti di formazione accreditati per selezionare i corsi più adatti, garantendo alta qualità e rilevanza del contenuto." },
  { n: "3", titolo: "Progettazione del Piano Formativo", desc: "In collaborazione con i responsabili aziendali, viene progettato un piano formativo su misura." },
  { n: "4", titolo: "Accesso a Finanziamenti", desc: "Grazie alle collaborazioni con enti bilaterali e fondi interprofessionali, facilitiamo l'accesso a finanziamenti per coprire i costi." },
  { n: "5", titolo: "Monitoraggio e Rendicontazione", desc: "Supporto continuo alle aziende durante il percorso formativo e assistenza nella fase di rendicontazione finale." },
  { n: "6", titolo: "Certificazione delle Competenze", desc: "Al termine, i dipendenti ricevono una certificazione che attesta le competenze acquisite." },
];

const BENEFICI = [
  "Formazione su Misura in base alle necessità specifiche della tua azienda",
  "Aggiornamenti Normativi e Tecnologici: le ultime novità in ambito normativo e gestionale",
  "Investimento nella crescita del team: personale più preparato, motivato e produttivo",
  "Fondi per la Formazione: riduci il carico economico accedendo ai fondi disponibili",
];

export default function AggiornamentoSpecializzazionePage() {
  return (
    <main id="main-content" data-testid="aggiornamento-specializzazione-page">
      <section className="relative h-[300px] md:h-[360px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&q=60&auto=format" alt="Formazione aggiornamento specializzazione" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.85)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <HeroBreadcrumb />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-2">Formazione</span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">Formazione di aggiornamento e specializzazione</h1>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-[var(--confcom-text-primary)] font-semibold font-['Encode_Sans_Semi_Condensed'] leading-relaxed mb-5">
            La formazione di aggiornamento e specializzazione è fondamentale per le aziende del Settore Terziario che desiderano mantenere il passo con le continue evoluzioni del mercato.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
            Promo.Ter e Confcommercio Roma offrono alle aziende l'opportunità di accedere a percorsi formativi di aggiornamento continuo, progettati per affinare le capacità dei lavoratori, migliorare la performance e renderli più competitivi. I corsi sono ideali per rispondere alle necessità di aggiornamento professionale, introducendo <strong className="text-[var(--confcom-text-primary)]">nuovi strumenti, tecnologie, normative e best practices</strong>.
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">Vantaggi</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {VANTAGGI.map((v) => (
              <div key={v.titolo} className="bg-white rounded-xl p-6 border border-[var(--confcom-border)]">
                <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{v.titolo}</h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">Come Funziona</h2>
              <div className="space-y-10">
                {STEPS.map((s) => (
                  <div key={s.n} className="flex gap-4">
                    <div className="w-8 h-8 bg-[var(--confcom-blue)] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm font-['Encode_Sans_Semi_Condensed']">{s.n}</div>
                    <div>
                      <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-1">{s.titolo}</h3>
                      <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed mb-4">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[var(--confcom-bg-subtle)] rounded-2xl p-8 border border-[var(--confcom-border)]">
              <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5">I Benefici Diretti per la Tua Azienda</h3>
              <div className="space-y-3">
                {BENEFICI.map((b) => (
                  <div key={b} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" />
                    <span className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[var(--confcom-blue)] rounded-2xl px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div>
              <p className="text-white font-bold text-xl font-['Encode_Sans_Semi_Condensed'] mb-2">Vuoi attivare subito il tuo percorso formativo o accedere a nuove opportunità? Contattaci!</p>
              <div className="text-white/75 text-sm space-y-1">
                <p>
                  <a href="mailto:commerciale.promoter@confcommercioroma.it" className="hover:text-white transition-colors">commerciale.promoter@confcommercioroma.it</a>
                  {" · "}
                  <a href="mailto:areagestione@confcommercioroma.it" className="hover:text-white transition-colors">areagestione@confcommercioroma.it</a>
                </p>
                <p>
                  <a href="tel:+390668437606" className="hover:text-white transition-colors">0668437 606</a>
                  {" · "}
                  <a href="tel:+390668437530" className="hover:text-white transition-colors">0668437 530</a>
                  {" · "}
                  <a href="tel:+390668437240" className="hover:text-white transition-colors">0668437 240</a>
                </p>
              </div>
            </div>
            <a href="https://forms.gle/98CnoTiUAdGHJ9e88" target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold text-sm hover:opacity-90 transition-opacity whitespace-nowrap">
              Richiedi maggiori informazioni <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
